import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { Card, Button, Upload, Checkbox, Spin, Tag, Space, List } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import hiringDocumentPackageService from "Services/HiringDocumentPackageService";
import {
  NextAction,
  PackageStatus,
  ItemStatusLabel,
  ItemStatusColor,
} from "Constants/Enums/HiringDocumentPackageEnum";

// Applicant-facing document package view (per Mudassir's Jun-16 handoff).
// Driven entirely off the package response. Renders the upload form when the
// applicant has work, a read-only status once submitted, or a wait message.
function ApplicantDocumentPackage({ jobApplicantId }) {
  const apiUrl = process.env.REACT_APP_API_URL;
  const fileBaseUrl = process.env.REACT_APP_Base_URL || apiUrl;

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState({}); // { [itemId]: File }
  const [ackPolicy, setAckPolicy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await hiringDocumentPackageService.getByApplicant(jobApplicantId);
      setPkg(data);
    } catch (e) {
      if (e.response?.status !== 404) {
        console.error("Error loading document package:", e);
        toast.error("Failed to load your documents");
      }
      setPkg(null);
    } finally {
      setLoading(false);
    }
  }, [jobApplicantId]);

  useEffect(() => {
    if (jobApplicantId) load();
  }, [jobApplicantId, load]);

  const documents = pkg?.documents || [];
  const uploadItems = documents.filter((d) => d.nextAction === NextAction.ApplicantSubmit);
  const ackItems = documents.filter((d) => d.nextAction === NextAction.ApplicantAcknowledge);
  const hasApplicantWork = uploadItems.length > 0 || ackItems.length > 0;

  const handleSubmit = async () => {
    const ids = uploadItems.map((d) => d.hiringDocumentItemId);
    if (ids.some((id) => !files[id])) {
      toast.warning("Please attach every required document");
      return;
    }
    if (ackItems.length > 0 && !ackPolicy) {
      toast.warning("Please acknowledge the HR Policy");
      return;
    }
    setBusy(true);
    try {
      await hiringDocumentPackageService.applicantSubmit(
        pkg.hiringDocumentPackageId,
        ids,
        ids.map((id) => files[id]),
        ackPolicy
      );
      toast.success("Documents submitted");
      setFiles({});
      setAckPolicy(false);
      await load();
    } catch (e) {
      console.error("Error submitting documents:", e);
      toast.error("Failed to submit documents");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }

  if (!pkg) {
    return (
      <Card title="Hiring Documents">
        <p>Your documents are being prepared. Please check back shortly.</p>
      </Card>
    );
  }

  // HR is still preparing the package (templates not submitted yet).
  if (pkg.status === PackageStatus.Draft) {
    return (
      <Card title="Hiring Documents">
        <p>The hiring team is preparing your documents. Please check back shortly.</p>
      </Card>
    );
  }

  // Nothing left for the applicant to do (submitted / in review / complete).
  if (!hasApplicantWork) {
    return (
      <Card title="Hiring Documents">
        <p style={{ marginBottom: 4 }}>
          Thank you. Your documents have been submitted and the hiring team will review them.
        </p>
        <List
          size="small"
          bordered
          dataSource={documents}
          renderItem={(d) => (
            <List.Item>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  gap: 8,
                }}
              >
                <span style={{ fontWeight: 500 }}>{d.displayName || d.documentType}</span>
                <Tag
                  color={ItemStatusColor[d.status] || "default"}
                  style={{ marginRight: 0 }}
                >
                  {ItemStatusLabel[d.status] || d.status}
                </Tag>
              </div>
            </List.Item>
          )}
        />
      </Card>
    );
  }

  // Applicant has documents to submit.
  return (
    <Card title="Submit Your Documents">
      <p style={{ marginBottom: 16 }}>
        Please upload each required document and acknowledge the HR Policy, then submit them all
        together.
      </p>
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {uploadItems.map((item) => (
          <div
            key={item.hiringDocumentItemId}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <div style={{ fontWeight: 500 }}>{item.displayName || item.documentType}</div>
              {item.hrTemplateFilePath && (
                <a
                  href={`${fileBaseUrl}/${item.hrTemplateFilePath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: "0.8rem" }}
                >
                  Download template to sign
                </a>
              )}
            </div>
            <Upload
              maxCount={1}
              beforeUpload={(file) => {
                setFiles((prev) => ({ ...prev, [item.hiringDocumentItemId]: file }));
                return false;
              }}
              onRemove={() =>
                setFiles((prev) => {
                  const next = { ...prev };
                  delete next[item.hiringDocumentItemId];
                  return next;
                })
              }
            >
              <Button icon={<UploadOutlined />} size="small">
                Attach
              </Button>
            </Upload>
          </div>
        ))}

        {ackItems.map((item) => (
          <div key={item.hiringDocumentItemId}>
            <Checkbox checked={ackPolicy} onChange={(e) => setAckPolicy(e.target.checked)}>
              I have read and acknowledge the {item.displayName || "HR Policy"}
            </Checkbox>
            {item.hrTemplateFilePath && (
              <a
                href={`${fileBaseUrl}/${item.hrTemplateFilePath}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginLeft: 8, fontSize: "0.8rem" }}
              >
                View
              </a>
            )}
          </div>
        ))}
      </Space>

      <Button type="primary" loading={busy} onClick={handleSubmit} style={{ marginTop: 16 }}>
        Submit Documents
      </Button>
    </Card>
  );
}

ApplicantDocumentPackage.propTypes = {
  jobApplicantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ApplicantDocumentPackage;
